import React, { useEffect, useState } from 'react';
import { Field, Form, FormikProvider, useFormik } from 'formik';
import { Button, Check, Input, Label, Modal } from 'edozvola-frontend-lib/dist/ui';
import './case-submission.scss';
import { AUTHORITIES, ERRORS, messages } from 'app/config/constants';
import { showToast } from 'app/shared/util/toast.util';
import { createCaseOrContinue } from 'app/edozvola/cases/service/cases.service';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { ConfirmationModal } from 'app/shared/modal/confirmation-modal';
import { useKeycloak } from 'app/modules/keycloak/keycloak';
import { Col, FormGroup, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';
import { CreateCaseOrContinueModel, initialCaseOrContinueValues } from 'app/edozvola/cases/cases.model';
import { PaginatedDropdown } from 'edozvola-frontend-lib/dist/ui/components/paginated-dropdown';
import * as Yup from 'yup';
import { queryClient } from 'app/app';
import {
  fetchAdministrativeUnitCodebookForPublicCaseCategoryQuery,
  fetchCaseCategoryCodebookForCaseGroupQuery,
  fetchCaseGroupCodebookQuery,
  fetchRequestWebIdsQuery,
  fetchSubjectTypeForCaseSubmissionQuery
} from 'app/edozvola/codebooks/codebooks.queries';
import AdministrativeUnitSearchModal from "app/edozvola/administrative-unit-overview/administrative-unit-search-modal";
import { initTabValidationErrors } from 'app/shared/reducers/validationerrors/validation-errors.reducer';

export const CaseSubmission = () => {
  const userAuthorities = useAppSelector(state => state.authentication.account.authorities);
  const account = useAppSelector(state => state.authentication.account);
  const hasAnonymousRole = userAuthorities && userAuthorities.includes(AUTHORITIES.NEPRIJAVLJENI);
  const szSubjectOfRepresentationSelected = useAppSelector(state => state.authentication.account.szSubjectOfRepresentationSelected);
  const [loginNiasAnonymousModalOpened, setLoginNiasAnonymousModalOpened] = useState(false);
  const [gdprModalOpened, setGdprModalOpened] = useState(false);
  const [gdprAgreed, setGdprAgreed] = useState(false);
  const [administrativeUnitSearchModalOpen, setAdministrativeUnitSearchModalOpen] = useState<boolean>(false);
  const navigate = useNavigate();
  const appDispatch = useAppDispatch();

  useEffect(() => {
    if (hasAnonymousRole) {
      setLoginNiasAnonymousModalOpened(true);
    } else {
      setLoginNiasAnonymousModalOpened(false);
    }
  }, [userAuthorities]);

  const validationSchema = Yup.object({
    requestType: Yup.array().of(Yup.object()).min(1, messages.MANDATORY_FIELD),
    caseCategory: Yup.array().when(['requestType'], (values, schema) => {
      return values[0][0].id === 1 ? schema.of(Yup.object()).min(1, messages.MANDATORY_FIELD) : schema;
    }),
    administrativeUnit: Yup.array().when(['requestType'], (values, schema) => {
      return values[0][0].id === 1 ? schema.of(Yup.object()).min(1, messages.MANDATORY_FIELD) : schema;
    }),
    requestWebId: Yup.array().when(['requestType'], (values, schema) => {
      return values[0][0].id === 2 && !hasAnonymousRole && requestWebIdOptions && requestWebIdOptions.length > 0
        ? schema.of(Yup.object()).min(1, messages.MANDATORY_FIELD)
        : schema;
    }),
    webId: Yup.object().when(['requestType'], (values, schema) => {
      return values[0][0].id === 2 ? Yup.string().required(messages.MANDATORY_FIELD) : Yup.string().notRequired();
    }),
    subjectType: Yup.array().when(['requestType', 'caseAsAssignee'], (values, schema) => {
      if (
        values[0][0].id === 1 &&
        (hasAnonymousRole || values[1] || (szSubjectOfRepresentationSelected && szSubjectOfRepresentationSelected === false))
      ) {
        return schema.of(Yup.object()).min(1, messages.MANDATORY_FIELD);
      }

      return schema;
    }),
    applicantOibOrMb: Yup.object().when(['requestType', 'caseAsAssignee', 'subjectType'], (values, schema) => {
      const isNewRequest = values[0][0]?.id === 1;
      const isContinueRequest = values[0][0]?.id === 2;
      const subjectType = values[2];

      if ((isNewRequest && (hasAnonymousRole || values[1])) || (isContinueRequest && hasAnonymousRole)) {
        if (subjectType[0]?.id === 1 || subjectType[0]?.id === 2) {
          return Yup.string()
            .required(messages.MANDATORY_FIELD)
            .matches(/^\d+$/, messages.ONLY_DIGITS)
            .min(11, messages.OIB_MIN_MAX_LENGTH)
            .max(11, messages.OIB_MIN_MAX_LENGTH);
        } else {
          return Yup.string()
            .required(messages.MANDATORY_FIELD)
            .matches(/^\d+$/, messages.ONLY_DIGITS)
            .max(11, messages.OIB_MIN_MAX_LENGTH);
        }
      }

      return Yup.string().notRequired();
    }),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: initialCaseOrContinueValues,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => handleSubmit(values, { setSubmitting }),
  });

  const { data: caseGroupCodebookOptions, isLoading: caseGroupCodebookOptionsLoading } = fetchCaseGroupCodebookQuery();
  const { data: caseCategoryCodebookOptions, isLoading: caseCategoryOptionsLoading } = fetchCaseCategoryCodebookForCaseGroupQuery(
    formik.values?.caseGroup,
    true,
    false,
    true,
  );
  const { data: administrativeUnitCodebookOptions, isLoading: administativeUnitOptionsLoading } =
    fetchAdministrativeUnitCodebookForPublicCaseCategoryQuery(formik.values?.caseCategory);
  const { data: subjectTypeOptions, isLoading: subjectTypeOptionsLoading } = fetchSubjectTypeForCaseSubmissionQuery();
  const { data: requestWebIdOptions, isLoading: requestWebIdOptionsLoading } = fetchRequestWebIdsQuery();

  const handleRequestTypeChange = selectedOption => {
    formik
      .setValues({
        ...initialCaseOrContinueValues,
        requestType: selectedOption,
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['codebook', 'case-category-for-case-group-query'] });
        queryClient.invalidateQueries({ queryKey: ['codebook', 'administrative-unit-for-case-category-query'] });
      });
  };

  const handleGroupChange = selectedOption => {
    formik
      .setValues({
        ...formik.values,
        caseGroup: selectedOption,
        caseCategory: [],
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['codebook', 'case-category-for-case-group-query'] });
      });
  };

  const handleCategoryChange = selectedOption => {
    formik
      .setValues({
        ...formik.values,
        caseCategory: selectedOption,
        administrativeUnit: [],
      })
      .then(() => {
        queryClient.invalidateQueries({ queryKey: ['codebook', 'administrative-unit-for-case-category-query'] });
      });
  };

  const handleRequestWebIdChange = selectedOption => {
    formik.setValues({
      ...formik.values,
      requestWebId: selectedOption,
      webId: selectedOption[0].id,
    });
  };

  const handleSubmitNewCaseOrContinue = () => {
    if (formik.values?.requestType[0].id === 1) {
      setGdprModalOpened(true);
    } else {
      const setSubmitting = formik.setSubmitting;
      handleSubmit(formik.values, { setSubmitting });
    }
  };

  const handleSubmit = (values: CreateCaseOrContinueModel, { setSubmitting }) => {
    setSubmitting(true);

    if (szSubjectOfRepresentationSelected && szSubjectOfRepresentationSelected === true) {
      values.caseAsAssignee = true;
      values.applicantOibOrMb = account.szIps;
    }

    if (values.requestType[0].id === 2) {
      values.continueEntry = true;
    }
    createCaseOrContinue(values)
      .then(data => {
        setSubmitting(false);
        appDispatch(initTabValidationErrors());
        navigate(`case/${data}`);
      })
      .catch(error => {
        setSubmitting(false);
        if (Object.values(ERRORS).includes(error.response?.data?.detail)) {
          showToast(toast.error, error.response?.data?.detail);
        } else {
          showToast(toast.error, ERRORS.GENERIC_ERROR);
        }
      });
  };

  const handleCaseAsAssigneeChange = (value: any) => {
    if (value === false) {
      formik.setFieldValue('subjectType', []);
      formik.setFieldValue('applicantOibOrMb', '');
    }

    formik.setFieldValue('caseAsAssignee', value);
  };

  return (
    <div className="container-fluid view-container">
      <Row>
        <Col md={12}>
          <div className={'mb-3 form-header'}>Predaja zahtjeva</div>
        </Col>
      </Row>

      <div className={'pt-1'}>
        <FormikProvider value={formik}>
          <Form className={'d-flex flex-column'}>
            <Row>
              <FormGroup className={'col-6'}>
                <Label labelText={'Odaberite tip zahtjeva'} />
                <PaginatedDropdown
                  onItemSelect={selectedOption => handleRequestTypeChange(selectedOption)}
                  selected={formik.values?.requestType}
                  labelKey={'value'}
                  options={[
                    { id: 1, value: 'Novi zahtjev' },
                    { id: 2, value: 'Nastavak unosa spremljenog zahtjeva' },
                  ]}
                  placeholder={'Odaberite tip zahtjeva'}
                />
              </FormGroup>
            </Row>
            {formik.values?.requestType && formik.values?.requestType[0].id === 1 && (
              <>
                <Row>
                  <FormGroup className={'col-6'}>
                    <Label labelText={'Grupa predmeta'} />
                    <PaginatedDropdown
                      onItemSelect={selectedOption => handleGroupChange(selectedOption)}
                      selected={formik.values.caseGroup}
                      labelKey={'value'}
                      options={caseGroupCodebookOptionsLoading ? [] : caseGroupCodebookOptions}
                      placeholder={'Odaberi grupu predmeta'}
                      clearButton
                    />
                  </FormGroup>
                </Row>
                <Row>
                  <FormGroup className={'col-6'}>
                    <Label labelText={'Vrsta predmeta'} />
                    <PaginatedDropdown
                      onItemSelect={selectedOption => handleCategoryChange(selectedOption)}
                      selected={formik.values.caseCategory}
                      labelKey={'value'}
                      options={caseCategoryOptionsLoading ? [] : caseCategoryCodebookOptions}
                      placeholder={'Odaberi vrstu predmeta'}
                      error={(formik.errors.caseCategory as string) ?? ''}
                      showErrorMessage
                    />
                  </FormGroup>
                </Row>
                <Row>
                  <FormGroup className={'col-6 d-flex flex-column'}>
                    <Row>
                      <Col>
                        <Label labelText={'Upravna jedinica'} />
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        <PaginatedDropdown
                          onItemSelect={selectedOptions => formik.setFieldValue('administrativeUnit', selectedOptions)}
                          selected={formik.values.administrativeUnit}
                          labelKey={'value'}
                          disabled={administrativeUnitCodebookOptions?.length === 0}
                          options={administativeUnitOptionsLoading ? [] : administrativeUnitCodebookOptions}
                          placeholder={'Odaberi upravnu jedinicu'}
                          error={(formik.errors.administrativeUnit as string) ?? ''}
                          showErrorMessage
                        />
                      </Col>
                    </Row>
                  </FormGroup>
                  <Col className={'col-3 align-items-end'}>
                    <Button
                      style={{marginTop: '31px'}}
                      label={'Pronađi nadležni ured'}
                      size={'lg'}
                      onClick={() => setAdministrativeUnitSearchModalOpen(true)}
                    />
                  </Col>
                </Row>
                {!hasAnonymousRole && !szSubjectOfRepresentationSelected && (
                  <Row>
                    <FormGroup className={'d-flex'}>
                      <div onClick={() => handleCaseAsAssigneeChange(!formik.values?.caseAsAssignee)} className={'hand d-flex'}>
                        <Check
                          className={'mb-2'}
                          id="caseAsAssignee"
                          name="caseAsAssignee"
                          checked={formik.values?.caseAsAssignee}
                          onChange={ev => handleCaseAsAssigneeChange(ev.target.checked)}
                        />
                        <span>Predajem zahtjev kao opunomoćenik</span>
                      </div>
                    </FormGroup>
                  </Row>
                )}
                {((!szSubjectOfRepresentationSelected && formik.getFieldMeta('caseAsAssignee').value) || hasAnonymousRole) && (
                  <>
                    <Row>
                        <FormGroup className={'col-6'}>
                          <Label labelText={'Podnositelj zahtjeva – vrsta subjekta'} />
                          <PaginatedDropdown
                            placeholder={'Odaberi podnositelja zahtjeva - vrstu subjekta'}
                            onItemSelect={selected => formik.setFieldValue('subjectType', selected)}
                            selected={formik.values.subjectType}
                            labelKey={'value'}
                            options={subjectTypeOptions}
                            isLoading={subjectTypeOptionsLoading}
                            disabled={!hasAnonymousRole && !formik.getFieldMeta('caseAsAssignee').value}
                            error={(formik.errors.subjectType as string) ?? ''}
                            showErrorMessage
                          />
                        </FormGroup>
                    </Row>
                    {formik.values?.subjectType && formik.values.subjectType[0] && (
                      <Row>
                        <FormGroup className={'col-6'}>
                          {formik.values?.subjectType &&
                            formik.values.subjectType[0] &&
                            (formik.values.subjectType[0]?.id === 1 || formik.values.subjectType[0].id === 2) && (
                              <Label labelText={'Podnositelj zahtjeva - OIB'} />
                            )}
                          {formik.values?.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0]?.id >= 3 && (
                            <Label labelText={'Podnositelj zahtjeva - MB'} />
                          )}
                          <Field
                            id="applicantOibOrMb"
                            name="applicantOibOrMb"
                            render={({ field }) => (
                              <Input
                                {...field}
                                type="input"
                                maxLength={11}
                                error={formik.errors.applicantOibOrMb}
                                placeholder={
                                  formik.values?.subjectType && formik.values.subjectType[0] && formik.values.subjectType[0]?.id >= 3
                                    ? 'Unesite MB podnositelja zahtjeva'
                                    : 'Unesite OIB podnositelja zahtjeva'
                                }
                                disabled={!hasAnonymousRole && !formik.getFieldMeta('caseAsAssignee').value}
                              />
                            )}
                          />
                        </FormGroup>
                      </Row>
                    )}
                  </>
                )}
              </>
            )}
            {formik.values?.requestType && formik.values?.requestType[0].id === 2 && (
              <>
                {!hasAnonymousRole && (
                  <FormGroup className={'col-6'}>
                    <Label labelText={'Šifra spremljenog zahtjeva'} />
                    <PaginatedDropdown
                      onItemSelect={selectedOptions => handleRequestWebIdChange(selectedOptions)}
                      selected={formik.values.requestWebId}
                      labelKey={'value'}
                      disabled={requestWebIdOptions?.length === 0}
                      options={requestWebIdOptionsLoading ? [] : requestWebIdOptions}
                      placeholder={requestWebIdOptions?.length === 0 ? 'Nemate spremljenih zahtjeva' : 'Odaberi šifru spremljenog zahtjeva'}
                      error={(formik.errors.requestWebId as string) ?? ''}
                      showErrorMessage
                    />
                  </FormGroup>
                )}
                {hasAnonymousRole && (
                  <>
                    <FormGroup className={'col-6'}>
                      <Label labelText={'Šifra spremljenog zahtjeva'} />
                      <Field
                        id="webId"
                        name="webId"
                        render={({ field }) => (
                          <Input {...field} type="input" error={formik.errors.webId} placeholder={'Unesite šifru spremljenog zahtjeva'} />
                        )}
                      />
                    </FormGroup>
                    <FormGroup className={'col-6'}>
                      <Label labelText={'OIB/MB podnositelja na zahtjevu'} />
                      <Field
                        id="applicantOibOrMb"
                        name="applicantOibOrMb"
                        render={({ field }) => (
                          <Input
                            {...field}
                            type="input"
                            maxLength={11}
                            error={formik.errors.applicantOibOrMb}
                            placeholder={'Unesite OIB/MB podnositelja na zahtjevu'}
                          />
                        )}
                      />
                    </FormGroup>
                  </>
                )}
              </>
            )}
            <FormGroup>
              <Button
                color={'danger'}
                onClick={handleSubmitNewCaseOrContinue}
                disabled={!formik.dirty || !formik.isValid}
                size={'lg'}
                label={'Nastavi'}
              />
            </FormGroup>
          </Form>
        </FormikProvider>
      </div>

      <ConfirmationModal
        modalOpened={loginNiasAnonymousModalOpened}
        header={'Želite li se prijaviti u sustav eGrađani?'}
        bodyMessage={`Poštovani, za korištenje eUsluge za izdavanjem akata za građenje molimo da se prijavite u sustav eGrađani. Potvrda o zaprimljenom zahtjevu bit će Vam poslana u korisnički pretinac nakon predaje zahtjeva.
          <p><p>
          Ako predate zahtjev bez prijave u sustav eGrađani, sve dokumente trebate dostaviti u Pisarnicu nadležnog tijela u roku 30 dana od datuma predaje zahtjeva.`}
        onConfirm={() => useKeycloak()?.login({ idpHint: 'saml-nias' })}
        onCancel={() => setLoginNiasAnonymousModalOpened(false)}
        confirmButtonLabel={'Prijavi se'}
        cancelButtonLabel={'Nastavi bez prijave'}
      />
      <Modal isOpen={gdprModalOpened} centered={true}>
        <ModalHeader>{'GDPR obavijest'}</ModalHeader>
        <ModalBody>
          <p
            dangerouslySetInnerHTML={{
              __html: `1. Podnositelj zahtjeva dobrovoljno daje osobne podatke u svrhu izdavanja akata za građenje tj. vođenja postupaka temeljem Zakona o prostornom uređenju i Zakona o gradnji. Dajem ovlasti Nadležnom gradskom upravnom tijelu da iste ima pravo provjeravati, obrađivati, čuvati i koristiti u skladu sa provedbenim propisima o zaštiti osobnih podataka, a koje može obrađivati samo u svrhu u koju su prikupljeni te se u druge svrhe neće koristiti.
              <p><p>
              2. Podnošenjem zahtjeva u elektroničkom obliku, daje se izričita privola za daljnju komunikaciiju u elektroničkom obliku. Dostava Rješenja i drugih akata Nadležnog tijela smatra se izvršenom sukladno odredbama Zakona o općem upravnom postupku, a u tu svrhu je potrebno imati otvoren korisnički pretinac na portalu e-Građani.
              <p><p>
              3. Pod kaznenom i materijalnom odgovornošću jamčim da su svi moji upisani podaci točni te da su svi dostavljeni dokumenti vjerodostojni.`,
            }}
          ></p>
          <FormGroup className={'d-flex'}>
            <div onClick={() => setGdprAgreed(!gdprAgreed)} className={'hand d-flex'}>
              <Check
                className={'mb-2'}
                id="gdprAgreed"
                name="gdprAgreed"
                checked={gdprAgreed}
                onChange={ev => setGdprAgreed(ev.target.checked)}
              />
              <span>Pročitao/la sam i suglasan/na sam s politikom zaštite osobnih podataka.</span>
            </div>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button
            size="lg"
            label={'Odustani'}
            onClick={() => {
              setGdprModalOpened(false);
              setGdprAgreed(false);
            }}
            color="secondary"
          />
          <Button
            label={'Nastavi'}
            size="lg"
            isLoading={formik.isSubmitting}
            disabled={!gdprAgreed}
            onClick={() => formik.submitForm()}
            color="info"
          />
        </ModalFooter>
      </Modal>
      <AdministrativeUnitSearchModal
        modal={administrativeUnitSearchModalOpen}
        closeModal={() => setAdministrativeUnitSearchModalOpen(false)}
        />
    </div>
  );
};

export default CaseSubmission;
